import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Check } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '../ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '../ui/command';
import { useFilm } from '../../context/FilmContext';
import { useFilmScenes } from '../../hooks/queries/useFilmScenes';
import { buildSceneSwitchUrl, extractDeepIds } from './urlHelpers';
import { cn } from '../../lib/utils';

/**
 * ScenePicker — the [Scene N · Title ▾] chip that ONLY renders when the
 * URL contains a scene id segment (e.g. legacy /scenes/:sceneId, or any
 * future /workspaces/W/films/F/scenes/:sceneId).
 *
 * Selecting a different scene swaps the scene id segment in-place, so the
 * user stays at the same depth (detail view, etc).
 */
export default function ScenePicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentFilmId } = useFilm();
  const [open, setOpen] = useState(false);

  const { sceneId } = useMemo(
    () => extractDeepIds(location.pathname),
    [location.pathname]
  );

  const scenesQuery = useFilmScenes(currentFilmId, { enabled: Boolean(currentFilmId && sceneId) });
  const scenes = scenesQuery.data || [];

  // No scene in URL — the chip is hidden entirely. (This matches AC #6.)
  if (!sceneId) return null;

  const current = scenes.find((s) => String(s.id) === String(sceneId));
  const sceneNumber = current?.scene_number ?? current?.number ?? null;
  const sceneTitle = current?.scene_name || current?.title || current?.name || '';
  const label = current
    ? sceneNumber != null
      ? `Scene ${sceneNumber}${sceneTitle ? ' · ' + sceneTitle : ''}`
      : sceneTitle || 'Scene'
    : 'Scene';

  const handleSelect = (id) => {
    setOpen(false);
    if (!id || String(id) === String(sceneId)) return;
    const url = buildSceneSwitchUrl(location.pathname, id);
    if (url) navigate(url);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Select scene"
          data-testid="ctx-switcher-scene-trigger"
          data-state={open ? 'open' : 'closed'}
          className={cn(
            'group inline-flex items-center gap-1.5 max-w-[200px] md:max-w-[260px]',
            'h-7 px-2 rounded-md text-xs font-medium',
            'text-muted-foreground hover:text-foreground hover:bg-muted/60',
            'data-[state=open]:bg-muted data-[state=open]:text-foreground',
            'transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
          )}
        >
          <span className="truncate">{label}</span>
          <ChevronDown
            className={cn(
              'h-3 w-3 shrink-0 opacity-60 transition-transform',
              'group-data-[state=open]:rotate-180'
            )}
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-80 p-0"
        data-testid="ctx-switcher-scene-popover"
      >
        <Command>
          <CommandInput placeholder="Search scenes…" aria-label="Search scenes" />
          <CommandList>
            <CommandEmpty>No scenes.</CommandEmpty>
            {scenesQuery.isLoading && (
              <div className="py-6 text-center text-xs text-muted-foreground">Loading scenes…</div>
            )}
            {scenes.length > 0 && (
              <CommandGroup heading="Scenes">
                {scenes.map((s) => {
                  const n = s.scene_number ?? s.number ?? '';
                  const t = s.title || s.name || '';
                  return (
                    <CommandItem
                      key={s.id}
                      value={`${n} ${t} ${s.id}`}
                      onSelect={() => handleSelect(s.id)}
                      data-testid={`ctx-switcher-scene-option-${s.id}`}
                    >
                      <span className="text-xs text-muted-foreground w-12 shrink-0">
                        {n !== '' ? `#${n}` : ''}
                      </span>
                      <span className="truncate flex-1">{t || 'Untitled'}</span>
                      {String(s.id) === String(sceneId) && (
                        <Check className="h-4 w-4 text-primary" aria-label="current" />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
